import { useAnalysisDetails } from './hooks/useAnalysisDetails';
import { AnalysisResults } from './AnalysisResults';
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExportButton } from './ExportButton';
import { FileText, Terminal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface AnalysisDetailsProps {
  analysisId: string;
  onClose: () => void;
}

export function AnalysisDetails({ analysisId, onClose }: AnalysisDetailsProps) {
  const { analysis, isLoading, error } = useAnalysisDetails(analysisId);
  const [candidateName, setCandidateName] = useState<string>('');
  const [jobTitle, setJobTitle] = useState<string>('');

  useEffect(() => {
    async function fetchDetails() {
      if (!analysis) return;
      try {
        const { data: candidateData } = await supabase
          .from('candidates')
          .select('name')
          .eq('id', analysis.candidate_id)
          .maybeSingle();
        const { data: jobData } = await supabase
          .from('jobs')
          .select('title')
          .eq('id', analysis.job_id)
          .maybeSingle();
        setCandidateName(candidateData?.name || 'Unknown Candidate');
        setJobTitle(jobData?.title || 'Unknown Position');
      } catch (fetchError) {
        console.error("Error fetching names for details view:", fetchError);
        setCandidateName('Error');
        setJobTitle('Error');
      }
    }
    fetchDetails();
  }, [analysis]);

  if (isLoading) {
    return (
      <div className="space-y-6 py-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-7 w-3/4" />
          <div className="flex gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <Alert variant="destructive" className="my-6">
        <Terminal className="h-4 w-4" />
        <AlertTitle>Error Loading Analysis</AlertTitle>
        <AlertDescription>{typeof error === 'string' ? error : error ? 'An unknown error occurred.' : 'Analysis data could not be found.'}</AlertDescription>
        <div className="mt-4">
          <Button variant="secondary" onClick={onClose}>
            Go Back
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary flex-shrink-0" />
          {isLoading ? <Skeleton className="h-6 w-48" /> : `${candidateName} × ${jobTitle} Report`}
        </h2>
        <div className="flex gap-2 flex-shrink-0">
          <ExportButton analysis={analysis} />
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
      <AnalysisResults analysis={analysis} />
    </div>
  );
}